import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useHermes } from "@/components/hermes/HermesProvider";
import {
  BrandSiteApiError,
  updateBrandSitePage,
  type BrandSitePage,
} from "@/lib/brand-site-api";
import {
  brandSiteSourceFileLabel,
  brandSiteSourceFilePath,
  isBrandSiteSourceDirty,
  resolveBrandSiteSourceSlug,
  sortBrandSiteSourcePages,
} from "@/lib/brand-site-source-editor";
import { cn } from "@/lib/utils";

const MonacoEditor = lazy(() => import("@monaco-editor/react"));

export type BrandSiteSourceEditorCoreProps = {
  accountId: string;
  brandSiteId: number;
  pages: BrandSitePage[];
  selectedSlug: string | null;
  onSelectSlug: (slug: string) => void;
  onSaved?: (page: BrandSitePage) => void;
  editorHeight?: string;
  showFullscreenButton?: boolean;
  onRequestFullscreen?: () => void;
  className?: string;
};

/** 品牌站静态页 HTML 源代码编辑（Monaco + 文件选择 + 保存）。 */
export function BrandSiteSourceEditorCore({
  accountId,
  brandSiteId,
  pages,
  selectedSlug,
  onSelectSlug,
  onSaved,
  editorHeight = "100%",
  showFullscreenButton = false,
  onRequestFullscreen,
  className,
}: BrandSiteSourceEditorCoreProps) {
  const { notifyBrandSiteDraftMutated } = useHermes();
  const sortedPages = useMemo(() => sortBrandSiteSourcePages(pages), [pages]);
  const activeSlug = resolveBrandSiteSourceSlug(sortedPages, selectedSlug);
  const activePage = sortedPages.find((page) => page.slug === activeSlug) ?? null;

  const [html, setHtml] = useState(activePage?.html_content ?? "");
  const [savedHtml, setSavedHtml] = useState(activePage?.html_content ?? "");
  const [saving, setSaving] = useState(false);
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);

  const dirty = isBrandSiteSourceDirty(savedHtml, html);

  useEffect(() => {
    if (!activePage) return;
    setHtml(activePage.html_content ?? "");
    setSavedHtml(activePage.html_content ?? "");
  }, [activePage?.id, activePage?.html_content]);

  const requestSlugChange = useCallback(
    (slug: string) => {
      if (slug === activeSlug) return;
      if (dirty) {
        setPendingSlug(slug);
        return;
      }
      onSelectSlug(slug);
    },
    [activeSlug, dirty, onSelectSlug],
  );

  async function save() {
    if (!activePage || saving) return;
    setSaving(true);
    try {
      const payload = await updateBrandSitePage(accountId, brandSiteId, activePage.id, {
        html_content: html,
        source: "human",
      });
      const updated = payload.page;
      setSavedHtml(updated.html_content ?? html);
      onSaved?.(updated);
      notifyBrandSiteDraftMutated({
        slug: updated.slug,
        page: updated,
      });
      toast.success("已保存草稿");
    } catch (caught) {
      const message = caught instanceof BrandSiteApiError ? caught.message : "保存失败";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border px-2 py-2">
        <Popover open={filePickerOpen} onOpenChange={setFilePickerOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 min-w-[180px] justify-between gap-2 px-2 font-normal"
              disabled={sortedPages.length === 0}
            >
              <span className="min-w-0 truncate text-left">
                {activePage ? brandSiteSourceFileLabel(activePage) : "选择文件"}
              </span>
              <ChevronsUpDown className="size-3.5 shrink-0 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[min(92vw,320px)] p-0">
            <Command>
              <CommandInput placeholder="搜索页面…" />
              <CommandList>
                <CommandEmpty>没有匹配的页面</CommandEmpty>
                <CommandGroup>
                  {sortedPages.map((page) => (
                    <CommandItem
                      key={page.id}
                      value={`${page.slug} ${page.title ?? ""} ${brandSiteSourceFilePath(page.slug)}`}
                      onSelect={() => {
                        setFilePickerOpen(false);
                        requestSlugChange(page.slug);
                      }}
                    >
                      <Check
                        className={cn(
                          "size-3.5",
                          page.slug === activeSlug ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm">{brandSiteSourceFileLabel(page)}</p>
                        <p className="truncate font-mono text-[11px] text-muted-foreground">
                          {brandSiteSourceFilePath(page.slug)}
                        </p>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <div className="ml-auto flex items-center gap-2">
          {dirty ? <span className="text-[11px] text-amber-600">未保存</span> : null}
          <Button type="button" size="sm" className="h-8" disabled={!activePage || saving || !dirty} onClick={() => void save()}>
            {saving ? "保存中…" : "保存草稿"}
          </Button>
          {showFullscreenButton && onRequestFullscreen ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 gap-1.5"
              onClick={onRequestFullscreen}
            >
              <Maximize2 className="size-3.5" />
              全屏
            </Button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        {activePage ? (
          <Suspense fallback={<p className="p-3 text-sm text-muted-foreground">编辑器加载中…</p>}>
            <MonacoEditor
              height={editorHeight}
              language="html"
              theme="vs"
              value={html}
              onChange={(value) => setHtml(value ?? "")}
              options={{
                minimap: { enabled: false },
                wordWrap: "on",
                fontSize: 13,
                automaticLayout: true,
              }}
            />
          </Suspense>
        ) : (
          <p className="p-4 text-sm text-muted-foreground">还没有可编辑的静态页。</p>
        )}
      </div>

      <ConfirmDialog
        open={pendingSlug != null}
        onOpenChange={(open) => {
          if (!open) setPendingSlug(null);
        }}
        title="放弃未保存的修改？"
        description="切换文件前请先保存，或确认丢弃当前编辑。"
        confirmLabel="丢弃并切换"
        tone="destructive"
        onConfirm={() => {
          if (pendingSlug) onSelectSlug(pendingSlug);
          setPendingSlug(null);
        }}
      />
    </div>
  );
}

type BrandSiteSourceEditorFullscreenProps = BrandSiteSourceEditorCoreProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function BrandSiteSourceEditorFullscreen({
  open,
  onOpenChange,
  ...coreProps
}: BrandSiteSourceEditorFullscreenProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(96vh,920px)] w-[min(96vw,1200px)] max-w-none flex-col gap-0 p-0 sm:max-w-none">
        <DialogHeader className="shrink-0 border-b border-border px-4 py-3 text-left">
          <DialogTitle>编辑源代码</DialogTitle>
          <DialogDescription>人工修改会写入草稿；AI 会话下次 read_page 会看到最新内容。</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col">
          <BrandSiteSourceEditorCore {...coreProps} editorHeight="100%" showFullscreenButton={false} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
